import { useContext, useMemo } from 'react';
import { type ChatContextItem, ChatContextType } from '@trinity/chat';
import { ActiveChatSessionContext } from '@trinity/chat-components/components/Chat/context';
import { useHeaderContext } from '../context';

/**
 * @param showSubjectInLabel When false, the label is always the context *type* (e.g. "Document"); use it
 * where the source's own name is rendered separately (ContextSubjectDisplay) so it isn't shown twice.
 */
export function useContextSelectoryDisplayValue({
  showSubjectInLabel = true,
}: { showSubjectInLabel?: boolean } = {}) {
  const {
    pendingContext,
    pendingContextType,
  } = useHeaderContext();
  const session = useContext(ActiveChatSessionContext);

  return useMemo(() => {
    if (pendingContext) {
      return getContextDisplayText(pendingContextType, pendingContext);
    }

    if (session.context?.type === ChatContextType.Agent) {
      return showSubjectInLabel
        ? (session.context.data.subject?.name ?? session.context.data.name)
        : session.context.data.name;
    }

    if (!showSubjectInLabel) {
      return getContextDisplayText(session.context?.type);
    }

    if (session.context?.type === ChatContextType.Folder) {
      return session.context.metadata?.name || 'Folder';
    }

    if (session.context?.type === ChatContextType.Document) {
      return session.context.metadata?.name || 'Document';
    }

    // Show the picked source's name, like an Agent context's subject does.
    if (session.context?.type === ChatContextType.Project) {
      return session.context.metadata?.name || 'Project';
    }

    if (session.context?.type === ChatContextType.Brand) {
      return session.context.metadata?.name || 'Brand';
    }

    if (session.context?.type === ChatContextType.Transcript) {
      return session.context.metadata?.name || 'Transcript';
    }

    return getContextDisplayText(session.context?.type);
  }, [showSubjectInLabel, pendingContext, pendingContextType, session.context.data, session.context?.type, session.context?.metadata]);
}

function getContextDisplayText(contextType: ChatContextType, context?: ChatContextItem): string {
  switch (contextType) {
    case ChatContextType.Document:
      return 'Document';
    case ChatContextType.Folder:
      return 'Folder';
    case ChatContextType.Transcript:
      return 'Transcript';
    case ChatContextType.Global:
      return 'Everything';
    case ChatContextType.KolProfile:
      return 'Profile';
    case ChatContextType.Agent: {
      return context.name;
    }
    case ChatContextType.Brand:
      return 'Brand';
    case ChatContextType.Project:
      return 'Project';
    case ChatContextType.MessageGeneration:
      return context?.name || 'Message Generation';
    case ChatContextType.Trini:
      return 'Trini';

    default:
      throw new UnreachableCaseError(contextType);
  }
}